import { pageNumber, tracker } from "./app-signals.js";
import { computed, effect, signal } from "alien-signals";
import { viewportHeight, viewportWidth } from "./platform/viewport-signals.js";
import { readerEvents } from "../constants.js";

export const pdfSize = signal({ width: 1024, height: 768 });
/* c8 ignore next 1 */
export const scale = signal(window.devicePixelRatio || 1);

export const pageNumberNext = computed(() => {
	return pageNumber() + 1;
});

export const pageCount = signal(0);

effect(() => {
	const page = pageNumber();
	const count = pageCount();
	if (page === 1) {
		document.documentElement.classList.add("cassis-pdf-start");
	} else {
		document.documentElement.classList.remove("cassis-pdf-start");
	}
	if (count !== 0 && page === count) {
		document.documentElement.classList.add("cassis-pdf-end");
	} else {
		document.documentElement.classList.remove("cassis-pdf-end");
	}
});

export const pageData = computed(() => {
	const viewport = pdfSize();
	const maxHeight = viewportHeight();
	const maxWidth = viewportWidth();
	const outputScale = scale();
	return calculatePageData(viewport, maxHeight, maxWidth, outputScale);
});

export function calculatePageData(viewport, maxHeight, maxWidth, outputScale) {
	let ratio = maxHeight / viewport.height;
	let spread = false;
	if (viewport.width * ratio >= maxWidth) {
		ratio = maxWidth / viewport.width;
	} else if (viewport.width * 2 * ratio <= maxWidth) {
		spread = true;
	}
	const count = pageCount();
	// Spreads pair pages (1, 2), (3, 4)…: switching to spread mode on an
	// even page (a rotation, a wider window) shows the spread containing
	// it, so that the previous and next spreads stay aligned.
	const first = spread ? spreadStart(pageNumber()) : pageNumber();
	const second = first + 1;
	// The last spread of an odd-length document has no second page.
	const pages =
		spread && (count === 0 || second <= count) ? [first, second] : [first];
	return {
		// ratio is used with the final getViewport to get correct page dimensions
		ratio,
		spread,
		pages,
		count,
		// outputScale, height, and width are used to size and resize (if outputScale isn't 1) the canvas elements
		outputScale,
		height: viewport.height * ratio,
		width: viewport.width * ratio,
	};
}

/** The odd page that starts the spread containing `page`. */
export function spreadStart(page) {
	return page > 1 ? page - ((page - 1) % 2) : page;
}

// Keeps pageNumber on the first page of the spread on screen, so that the
// page position and bookmarks name the page shown on the left. This is
// not a page turn and records nothing with the tracker.
effect(() => {
	const { spread } = pageData();
	if (!spread) return;
	const page = pageNumber();
	const start = spreadStart(page);
	if (start !== page) pageNumber(start);
});

/**
 * Records a page turn with the tracker: `p` is the first page on screen
 * over the page count, clamped to [0, 1] (the contract's definition; with
 * `sp` set the next page is on screen too), plus `pg`, that first page,
 * and `pc`, the page count.
 */
function trackPageTurn(event) {
	const trackerService = tracker();
	const { spread, pages, count } = pageData();
	if (!trackerService || !count) return;
	const progress = Math.min(Math.max(pages[0] / count, 0), 1);
	trackerService.handleProgression(
		event,
		progress,
		spread,
		undefined,
		pages[0],
		count,
	);
}

/**
 * Moves to `target`, the first page to show. Navigation before the page
 * count is known, or to a page that is not a finite number, does nothing:
 * both used to put NaN, 0 or negative numbers into pageNumber.
 */
function moveTo(target) {
	const count = pageCount();
	if (!count || !Number.isFinite(target)) return;
	const { spread } = pageData();
	let page = Math.min(Math.max(Math.round(target), 1), count);
	if (spread) {
		page = spreadStart(page);
	}
	const current = pageNumber();
	if (page === current) return;
	pageNumber(page);
	trackPageTurn(
		page > current
			? readerEvents.PageTurnForward
			: readerEvents.PageTurnBackward,
	);
}

export function goToNext() {
	const { spread, pages, count } = pageData();
	const target = pages[0] + (spread ? 2 : 1);
	// At the last page (or spread) there is nowhere to go.
	if (target > count) return;
	moveTo(target);
}

export function goToPrevious() {
	const { spread, pages } = pageData();
	moveTo(Math.max(pages[0] - (spread ? 2 : 1), 1));
}

export function goToStart() {
	goToPage(1);
}
export function goToEnd() {
	goToPage(pageCount());
}

export function goToPage(targetNumber) {
	moveTo(targetNumber);
}

// spread
// page1 page2
// pdfViewport
// outputScale
// height
// width
